<script setup>
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import ColorField from '../../fields/ColorField.vue';
import SpacingField from '../../fields/SpacingField.vue';
import SliderField from '../../fields/SliderField.vue';
import FontField from '../../fields/FontField.vue';
import AlignField from '../../fields/AlignField.vue';
import VisibilityField from '../../fields/VisibilityField.vue';
import { withScheme } from '../../document/links';

const props = defineProps({
  node: { type: Object, required: true },
  // Elemento do rodapé fica sempre visível (o servidor força): sem o campo.
  selection: { type: Object, default: null },
});
const emit = defineEmits(['update']);
const ed = inject('emailEditor');
const { t } = useI18n();
// Ao sair do campo, domínio sem esquema ganha https:// (sem mexer enquanto digita).
const onLinkBlur = value => {
  const fixed = withScheme(value);
  if (fixed !== value) emit('update', { url: fixed }, 'url');
};
const theme = computed(() => ed.template.value?.theme || {});
const fonts = computed(() => ed.catalog.value?.layout?.fonts);
const st = computed(() => props.node.style || {});
const style = (key, value) => emit('update', { style: { [key]: value } }, key);
const inner = computed(() => st.value.padding_inner || [14, 28]);
</script>

<template>
  <div data-props="button" class="flex flex-col gap-4">
    <label data-field="label" class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.EDITOR.PROPS.LABEL')
      }}</span>
      <input
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm"
        maxlength="120"
        :value="node.label"
        @input="emit('update', { label: $event.target.value }, 'label')"
      />
    </label>
    <label data-field="url" class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.EDITOR.PROPS.URL')
      }}</span>
      <input
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm"
        :value="node.url"
        @input="emit('update', { url: $event.target.value }, 'url')"
        @blur="onLinkBlur($event.target.value)"
      />
    </label>
    <ColorField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.BUTTON_BG')"
      :theme="theme"
      :model-value="st.bg"
      @update:model-value="v => style('bg', v)"
    />
    <ColorField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.BUTTON_TEXT')"
      :theme="theme"
      allow-auto
      :model-value="st.color"
      @update:model-value="v => style('color', v)"
    />
    <FontField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.FONT')"
      :fonts="fonts"
      :model-value="st.font_family"
      @update:model-value="v => style('font_family', v)"
    />
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.SIZE')"
      :model-value="st.font_size"
      :min="8"
      :max="40"
      unit="px"
      @update:model-value="v => style('font_size', v)"
    />
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.INNER_PADDING')"
      :model-value="inner[1]"
      :min="4"
      :max="80"
      unit="px"
      @update:model-value="
        v => style('padding_inner', [Math.max(4, Math.round(v / 2)), v])
      "
    />
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.RADIUS')"
      :model-value="st.radius"
      :min="0"
      :max="60"
      unit="px"
      @update:model-value="v => style('radius', v)"
    />
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.BORDER_WIDTH')"
      :model-value="st.border?.width || 0"
      :min="0"
      :max="10"
      unit="px"
      @update:model-value="
        v =>
          style(
            'border',
            v
              ? { width: v, color: st.border?.color || '#e6e9ee', sides: 'all' }
              : null
          )
      "
    />
    <label class="flex items-center gap-2 text-xs text-n-slate-12">
      <input
        type="checkbox"
        :checked="st.full_width"
        @change="style('full_width', $event.target.checked)"
      />
      {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.FULL_WIDTH') }}
    </label>
    <AlignField
      :model-value="st.align"
      @update:model-value="v => style('align', v)"
    />
    <SpacingField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.PADDING')"
      :model-value="st.padding"
      @update:model-value="v => style('padding', v)"
    />
    <VisibilityField
      v-if="selection?.row?.role !== 'footer'"
      :model-value="st.hide_on"
      @update:model-value="v => style('hide_on', v)"
    />
  </div>
</template>
